import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  MapPreview,
  useApp,
} from '@startupquickstart/frontend';
import { Check, MapPin, Pencil, Volume2, X } from 'lucide-react';
import { useState } from 'react';

import {
  challengeTypeLabel,
  hasMeaningfulAnswerType,
} from '@/components/challenges/challengeKinds';
import { useUpdateCoords } from '@/components/events/map/useUpdateCoords';
import { PinLocationDialog } from '@/components/shared/map/PinLocationDialog';
import { useChallengeDetailContext } from './challengeDetailContext';

// Match the event Overview tab's "Event Info" panel so all
// three detail pages (event / team / challenge) read as one
// consistent design. !pb-4 overrides shadcn's [.border-b]:pb-6
// so the header padding is symmetric (4 top, 4 bottom).
const CARD_CLASSES = 'rounded-xl border bg-card shadow-sm py-0 gap-0';
const CARD_HEADER_CLASSES = 'px-6 py-4 !pb-4 border-b';
const CARD_CONTENT_CLASSES = 'p-6';

const ANSWER_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  photo: 'Photo',
  video: 'Video',
  true_false: 'True / False',
  multiple_choice: 'Multiple Choice',
  fill_in_the_blank: 'Fill in the Blank',
  puzzle: 'Puzzle',
};

/**
 * Read-only summary of the challenge: stats, content, the canonical
 * answer + alternates (so a facilitator can verify what counts as
 * correct), the location map when set, and any media references.
 * The hero header above already shows the QR + access-code badge,
 * so this tab focuses on the body of the challenge.
 */
export function EventChallengeOverviewTab() {
  const { challenge, eventId } = useChallengeDetailContext();
  const { config } = useApp();
  const updateCoords = useUpdateCoords(eventId);
  const [editingLocation, setEditingLocation] = useState(false);

  const hasLocation = challenge.latitude != null && challenge.longitude != null;
  const hasMedia = !!(challenge.imageUrl || challenge.videoUrl || challenge.audioUrl);
  // Photo/video challenges use AI grading instead of a literal answer,
  // so we only show the Answer card when there's something to show.
  const hasAnswer =
    !!challenge.answer ||
    (challenge.alternateAnswers && challenge.alternateAnswers.length > 0) ||
    (challenge.choices && challenge.choices.length > 0) ||
    !!challenge.aiContext;

  return (
    <div className={hasLocation ? 'grid gap-6 lg:grid-cols-3' : 'space-y-6'}>
      {/* Main column — challenge info, clue, answer, hint, access code,
          and any media. Spans two columns on desktop when the
          location sidebar is showing. */}
      <div className={hasLocation ? 'space-y-6 lg:col-span-2' : 'space-y-6'}>
        <Card className={CARD_CLASSES}>
          <CardHeader className={CARD_HEADER_CLASSES}>
            <CardTitle>Challenge Info</CardTitle>
            <CardDescription>Type, scoring, and unlock requirements</CardDescription>
          </CardHeader>
          <CardContent className={CARD_CONTENT_CLASSES}>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Type" value={challengeTypeLabel(challenge.type)} />
              <Field label="Difficulty" value={challenge.difficulty} />
              <Field label="Points" value={String(challenge.points)} />
              {/* Answer type is meaningless for roadblock + minigames (the Type
                  drives them), so only show it where it applies. */}
              {challenge.answerType && hasMeaningfulAnswerType(challenge.type) && (
                <Field
                  label="Answer Type"
                  value={ANSWER_TYPE_LABELS[challenge.answerType] || challenge.answerType}
                />
              )}
              {challenge.zone && <Field label="Zone" value={challenge.zone} />}
              {challenge.hintCost > 0 && (
                <Field label="Hint Cost" value={`${challenge.hintCost} pts`} />
              )}
              {challenge.accessCode && (
                <Field label="Access Code" value={challenge.accessCode} mono />
              )}
              {challenge.maxCompletions != null && (
                <Field label="Max Completions" value={String(challenge.maxCompletions)} />
              )}
              {challenge.isStarter && <Field label="Starter" value="Yes" />}
              {challenge.isTimed && <Field label="Timed" value="Yes" />}
              {challenge.puzzleGridSize != null && (
                <Field
                  label="Puzzle Grid"
                  value={`${challenge.puzzleGridSize} × ${challenge.puzzleGridSize}`}
                />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className={CARD_CLASSES}>
          <CardHeader className={CARD_HEADER_CLASSES}>
            <CardTitle>Clue</CardTitle>
            <CardDescription>What players see when this challenge unlocks</CardDescription>
          </CardHeader>
          <CardContent className={CARD_CONTENT_CLASSES}>
            <p className="whitespace-pre-wrap text-base text-foreground">{challenge.clue}</p>
          </CardContent>
        </Card>

        {challenge.description && (
          <Card className={CARD_CLASSES}>
            <CardHeader className={CARD_HEADER_CLASSES}>
              <CardTitle>Description</CardTitle>
              <CardDescription>Internal notes on this challenge</CardDescription>
            </CardHeader>
            <CardContent className={CARD_CONTENT_CLASSES}>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {challenge.description}
              </p>
            </CardContent>
          </Card>
        )}

        {hasAnswer && (
          <Card className={CARD_CLASSES}>
            <CardHeader className={CARD_HEADER_CLASSES}>
              <CardTitle>Answer</CardTitle>
              <CardDescription>
                {challenge.answerType === 'fill_in_the_blank'
                  ? 'Canonical answer plus any alternates the validator accepts'
                  : challenge.answerType === 'multiple_choice'
                    ? 'All choices presented to players, with the correct one marked'
                    : challenge.answerType === 'photo' || challenge.answerType === 'video'
                      ? 'Free-form context the AI uses when grading submissions'
                      : 'What players need to submit to score points'}
              </CardDescription>
            </CardHeader>
            <CardContent className={`${CARD_CONTENT_CLASSES} space-y-4`}>
              {challenge.answer && (
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">Canonical answer</p>
                  <code className="inline-block rounded bg-muted px-3 py-1.5 font-mono text-sm">
                    {challenge.answer}
                  </code>
                </div>
              )}

              {challenge.alternateAnswers && challenge.alternateAnswers.length > 0 && (
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">
                    Alternate answers ({challenge.alternateAnswers.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {challenge.alternateAnswers.map((alt, i) => (
                      <code
                        key={`${alt}-${i}`}
                        className="rounded bg-muted px-2 py-1 font-mono text-xs">
                        {alt}
                      </code>
                    ))}
                  </div>
                </div>
              )}

              {challenge.answerType === 'multiple_choice' &&
                challenge.choices &&
                challenge.choices.length > 0 && (
                  <div>
                    <p className="mb-1 text-sm text-muted-foreground">Choices</p>
                    <ul className="space-y-1.5">
                      {challenge.choices.map((choice, i) => {
                        const isCorrect = choice === challenge.answer;
                        return (
                          <li
                            key={`${choice}-${i}`}
                            className={
                              isCorrect
                                ? 'flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 ring-1 ring-emerald-200'
                                : 'flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm text-muted-foreground'
                            }>
                            {isCorrect ? (
                              <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                            ) : (
                              <X className="h-4 w-4 shrink-0 text-muted-foreground/50" />
                            )}
                            <span className="font-mono">{choice}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

              {challenge.aiContext && (
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">AI grading context</p>
                  <p className="whitespace-pre-wrap rounded-lg bg-muted/50 p-3 text-sm text-foreground">
                    {challenge.aiContext}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {challenge.hint && (
          <Card className={CARD_CLASSES}>
            <CardHeader className={CARD_HEADER_CLASSES}>
              <CardTitle>Hint</CardTitle>
              <CardDescription>
                {challenge.hintCost > 0
                  ? `Costs ${challenge.hintCost} pts when used`
                  : 'Free hint'}
              </CardDescription>
            </CardHeader>
            <CardContent className={CARD_CONTENT_CLASSES}>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {challenge.hint}
              </p>
            </CardContent>
          </Card>
        )}

        {challenge.accessCode && (
          <Card className={CARD_CLASSES}>
            <CardHeader className={CARD_HEADER_CLASSES}>
              <CardTitle>Access Code Prompt</CardTitle>
              <CardDescription>
                Players must submit this code to unlock the challenge
              </CardDescription>
            </CardHeader>
            <CardContent className={`${CARD_CONTENT_CLASSES} space-y-2`}>
              <code className="inline-block rounded bg-muted px-2 py-1 font-mono text-sm">
                {challenge.accessCode}
              </code>
              {challenge.accessCodeMessage && (
                <p className="text-sm text-muted-foreground">{challenge.accessCodeMessage}</p>
              )}
            </CardContent>
          </Card>
        )}

        {hasMedia && (
          <Card className={CARD_CLASSES}>
            <CardHeader className={CARD_HEADER_CLASSES}>
              <CardTitle>Media</CardTitle>
              <CardDescription>
                Reference image, video, and audio attached to this challenge
              </CardDescription>
            </CardHeader>
            <CardContent className={`${CARD_CONTENT_CLASSES} space-y-4`}>
              {challenge.imageUrl && (
                <div>
                  <p className="mb-2 text-sm text-muted-foreground">Image</p>
                  <img
                    src={challenge.imageUrl}
                    alt={challenge.name}
                    className="max-h-64 rounded-lg object-cover"
                  />
                </div>
              )}
              {challenge.videoUrl && (
                <div>
                  <p className="mb-2 text-sm text-muted-foreground">Video URL</p>
                  <a
                    href={challenge.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-sm text-primary hover:underline">
                    {challenge.videoUrl}
                  </a>
                </div>
              )}
              {challenge.audioUrl && (
                <div>
                  <p className="mb-2 text-sm text-muted-foreground">Audio voiceover</p>
                  <div className="flex items-center gap-3">
                    <Volume2 className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <audio controls src={challenge.audioUrl} className="w-full" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sidebar — location map. Renders only when the challenge has
          coordinates so the page stays single-column otherwise. */}
      {hasLocation && (
        <div className="space-y-6 lg:col-span-1">
          <Card className={`${CARD_CLASSES} overflow-hidden`}>
            <CardHeader className={CARD_HEADER_CLASSES}>
              <CardTitle>Location</CardTitle>
              <CardDescription>Where players need to be to complete this</CardDescription>
            </CardHeader>
            <MapPreview
              latitude={challenge.latitude}
              longitude={challenge.longitude}
              googleMapsApiKey={config?.googleMapsApiKey}
              alt={`Map of ${challenge.name}`}
              height="h-56"
              className="rounded-none"
              fallbackText="Map unavailable"
            />
            <CardContent className={`${CARD_CONTENT_CLASSES} space-y-3`}>
              {challenge.zone && (
                <div>
                  <p className="mb-0.5 text-xs text-muted-foreground">Zone</p>
                  <p className="text-sm font-medium">{challenge.zone}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="mb-0.5 text-xs text-muted-foreground">Latitude</p>
                  <p className="text-sm font-mono">{challenge.latitude}</p>
                </div>
                <div>
                  <p className="mb-0.5 text-xs text-muted-foreground">Longitude</p>
                  <p className="text-sm font-mono">{challenge.longitude}</p>
                </div>
              </div>
              <div className="border-t pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  icon={Pencil}
                  onClick={() => setEditingLocation(true)}
                >
                  Edit location
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* No pin yet — let the facilitator drop one on the map. */}
      {!hasLocation && (
        <Button variant="outline" size="sm" icon={MapPin} onClick={() => setEditingLocation(true)}>
          Set location
        </Button>
      )}

      <PinLocationDialog
        open={editingLocation}
        onOpenChange={setEditingLocation}
        latitude={challenge.latitude ?? null}
        longitude={challenge.longitude ?? null}
        googleMapsApiKey={config?.googleMapsApiKey}
        saving={updateCoords.isPending}
        onSave={(latitude, longitude) =>
          updateCoords.mutate(
            { kind: 'challenge', entityId: challenge.id, latitude, longitude },
            { onSuccess: () => setEditingLocation(false) },
          )
        }
      />
    </div>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="mb-1 text-sm text-muted-foreground">{label}</p>
      <p className={`text-sm font-medium ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  );
}
